import { Alert, Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { API } from "aws-amplify";
import { useState } from "react";
import { fetchAllLLMProjects } from "../../dataaccess/fetchAllLLMProjects";
import { apiKeyManagementHeaders } from "../../utils/apiKeyManagementAuth";

type MigrationSummary = Record<string, number>;
type MigrationResult = {
  projectId: string;
  projectName?: string;
  status: string;
  mismatchedSlots?: string[];
  unverifiedSlots?: string[];
};

const addSummary = (target: MigrationSummary, source: MigrationSummary = {}) => {
  for (const [status, count] of Object.entries(source)) {
    target[status] = (target[status] || 0) + count;
  }
};

export default function AdminApiKeyMigration() {
  const [running, setRunning] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<MigrationSummary | null>(null);
  const [results, setResults] = useState<MigrationResult[]>([]);
  const [error, setError] = useState("");

  const runMigration = async () => {
    setRunning(true);
    setProcessed(0);
    setTotal(0);
    setSummary(null);
    setResults([]);
    setError("");

    try {
      const projects = await fetchAllLLMProjects({ activeOnly: false });
      const projectIds = projects.map((project) => project.id).filter(Boolean);
      const projectNames = new Map(
        projects.map((project) => [project.id, project.name || project.id])
      );
      setTotal(projectIds.length);

      const combined: MigrationSummary = {};
      for (let offset = 0; offset < projectIds.length; offset += 20) {
        const batch = projectIds.slice(offset, offset + 20);
        const response = await API.post(
          "eventapi",
          "/migrateIntegrationApiKeys",
          {
            headers: await apiKeyManagementHeaders(),
            body: { projectIds: batch },
          }
        );
        addSummary(combined, response?.summary);
        setResults((current) => [
          ...current,
          ...(response?.results || []).map((result: MigrationResult) => ({
            ...result,
            projectName: projectNames.get(result.projectId),
          })),
        ]);
        setProcessed(Math.min(offset + batch.length, projectIds.length));
        setSummary({ ...combined });
      }
    } catch (migrationError: any) {
      setError(migrationError?.message || "Integration API key migration failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Typography variant="h5" gutterBottom>
        Integration API key migration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Copy existing integration API key verifiers into each LLMProject. The
        migration is idempotent, does not rotate keys, and does not delete the
        legacy secrets. New and rotated keys are stored as hashes only.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Only secrets named project-&lt;project-id&gt;-integration-api-keys are
        migration candidates. Never delete project-&lt;project-id&gt;-api-keys,
        which contain provider credentials.
      </Alert>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" onClick={runMigration} disabled={running}>
          {running ? "Migrating..." : "Migrate integration API keys"}
        </Button>
        {total > 0 && (
          <Typography variant="body2">
            {processed} / {total} projects processed
          </Typography>
        )}
      </Stack>

      {running && total > 0 && (
        <LinearProgress
          variant="determinate"
          value={(processed / total) * 100}
          sx={{ mt: 2 }}
        />
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {summary && (
        <Alert
          severity={summary.failed || summary["hash-mismatch"] ? "warning" : "success"}
          sx={{ mt: 2 }}
        >
          Verified legacy keys: {summary.verified || 0}; already hash-only: {summary["no-secret-indexed"] || 0}; no keys (expected): {summary["no-keys"] || 0}; hash mismatches: {summary["hash-mismatch"] || 0}; failed: {summary.failed || 0}.
        </Alert>
      )}
      {results.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 2 }}>
          {results.map((result) => (
            <Typography key={result.projectId} variant="body2">
              {result.projectName || result.projectId}: {result.status}
              {result.status === "verified"
                ? ` — legacy secret: project-${result.projectId}-integration-api-keys`
                : ""}
              {result.mismatchedSlots?.length
                ? ` (${result.mismatchedSlots.join(", ")})`
                : result.unverifiedSlots?.length
                  ? ` (${result.unverifiedSlots.join(", ")})`
                  : ""}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}
